import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { getSnapTradeAccounts, getSnapTradeHoldings, syncSnapTradeHoldings } from "../../api/snaptrade";
import {
  listManualAssets,
  createManualAsset,
  deleteManualAsset,
  TYPE_TO_ASSET_CLASS,
  ASSET_CLASS_TO_TYPE,
} from "../../api/manualAssetsApi";
import { AuthContext } from "../../context/AuthContext";
import Button from "../../components/Button";
import {
  convertAmount,
  formatCurrency as formatCurrencyShared,
  resolveDisplayCurrency,
  CURRENCY_RATES_VS_USD,
} from "../../utils/currency";
import DocumentUploadModal from "../../components/DocumentUploadModal";
import WatchlistCard from "../../components/WatchlistCard";

const ASSET_TYPES = [
  "Stocks",
  "Mutual Funds",
  "ETF",
  "Real Estate / Land",
  "Bonds",
  "Cash / Savings",
  "Gold / Commodities",
  "Crypto",
  "Private Business",
  "Other",
];

const EMPTY_MANUAL_ASSET = {
  name: "",
  symbol: "",
  type: "Real Estate / Land",
  currentValue: "",
  costBasis: "",
  currency: "USD",
  purchaseDate: "",
  location: "",
  area: "",
  areaUnit: "sq ft",
  localPricePerUnit: "",
  notes: "",
};

// Use the shared rate map so we don't drift out of sync across pages.
const CURRENCY_TO_USD = CURRENCY_RATES_VS_USD;

const INDIAN_SECTOR_MAP = {
  CANBK: "Banking & Finance",
  RELIANCE: "Energy & Conglomerate",
  HINDCOPPER: "Metals & Mining",
  AFFLE: "Technology",
  ADANIGREEN: "Renewable Energy",
  JIOFIN: "Banking & Finance",
  HFCL: "Telecommunications",
};

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Convert a native amount to USD via the shared utility (USD is still the
// internal aggregation currency; the user-facing summary is then re-projected
// into their preferred display currency below).
const toUsd = (value, currency) => convertAmount(value || 0, currency || "USD", "USD");

const getHoldingYears = (purchaseDate) => {
  if (!purchaseDate) return 1;
  const start = new Date(purchaseDate);
  if (Number.isNaN(start.getTime())) return 1;
  const years = (Date.now() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  return Math.max(years, 0.1);
};

const formatCurrency = (value, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatPercent = (value) => `${value >= 0 ? "+" : ""}${(value || 0).toFixed(2)}%`;

const normalizeBrokerageHolding = (holding) => {
  const cleanSymbol = holding.symbol?.trim().split(".")[0].toUpperCase();
  const sector = INDIAN_SECTOR_MAP[cleanSymbol] || holding.sector || "Other";
  const marketValue = numberValue(holding.marketValue);
  const costBasis = numberValue(holding.costBasis);
  const unrealizedPL = numberValue(holding.unrealizedPL);

  return {
    ...holding,
    id: `linked-${holding.accountId || holding.account || "account"}-${holding.symbol}`,
    source: "Linked Account",
    assetType: holding.assetType || "Stocks",
    sector,
    marketValue,
    costBasis,
    unrealizedPL,
    unrealizedPLPercent: costBasis > 0 ? (unrealizedPL / costBasis) * 100 : numberValue(holding.unrealizedPLPercent),
    valueUSD: toUsd(marketValue, holding.currency || "USD"),
    costBasisUSD: toUsd(costBasis, holding.currency || "USD"),
    unrealizedPLUSD: toUsd(unrealizedPL, holding.currency || "USD"),
    holdingYears: 1,
  };
};

const normalizeAccountBalance = (account) => {
  const currencyCode = account.currency || "USD";
  const balance = numberValue(account.balance);
  const brokerage = account.brokerage || account.name || "Linked Account";

  return {
    id: `account-cash-${account.id || brokerage}`,
    source: "Linked Account",
    symbol: `${brokerage.slice(0, 4).toUpperCase()} CASH`,
    name: `${brokerage} Available Balance`,
    account: brokerage,
    accountId: account.id || brokerage,
    quantity: 1,
    price: balance,
    marketValue: balance,
    costBasis: balance,
    unrealizedPL: 0,
    unrealizedPLPercent: 0,
    currency: currencyCode,
    sector: "Cash / Savings",
    assetType: "Cash / Savings",
    valueUSD: toUsd(balance, currencyCode),
    costBasisUSD: toUsd(balance, currencyCode),
    unrealizedPLUSD: 0,
    holdingYears: 1,
    notes: "Available cash balance from linked account",
  };
};

// Convert a backend ManualAsset row into the holding shape this page renders.
// Class-specific extras (location, area, area unit, local price per unit) live
// in `metadata` server-side; pull them back out for the UI here.
const normalizeManualAsset = (asset) => {
  const meta = asset.metadata || {};
  const currentValue = numberValue(asset.current_value);
  const costBasis = numberValue(asset.cost_basis);
  const unrealizedPL = currentValue - costBasis;
  const typeLabel = ASSET_CLASS_TO_TYPE[asset.asset_class] || "Other";
  const symbol = asset.symbol?.trim() || asset.name?.trim()?.slice(0, 6).toUpperCase() || "ASSET";
  const landDetails = [meta.location, meta.area ? `${meta.area} ${meta.areaUnit || "sq ft"}` : ""]
    .filter(Boolean)
    .join(" | ");

  return {
    id: asset.id,
    source: "Manual",
    symbol,
    name: asset.name || symbol,
    account: "Manual Assets",
    accountId: "manual",
    quantity: 1,
    price: currentValue,
    marketValue: currentValue,
    costBasis,
    unrealizedPL,
    unrealizedPLPercent: costBasis > 0 ? (unrealizedPL / costBasis) * 100 : 0,
    currency: asset.currency || "USD",
    sector: typeLabel,
    assetType: typeLabel,
    valueUSD: toUsd(currentValue, asset.currency || "USD"),
    costBasisUSD: toUsd(costBasis, asset.currency || "USD"),
    unrealizedPLUSD: toUsd(unrealizedPL, asset.currency || "USD"),
    holdingYears: getHoldingYears(asset.acquired_at),
    notes: [landDetails, asset.notes].filter(Boolean).join(" - "),
    purchaseDate: asset.acquired_at || "",
    location: meta.location || "",
    area: numberValue(meta.area),
    areaUnit: meta.areaUnit || "sq ft",
    localPricePerUnit: numberValue(meta.localPricePerUnit),
  };
};

const Holdings = () => {
  const { profile } = useContext(AuthContext);
  const displayCurrency = resolveDisplayCurrency(profile);
  const [linkedHoldings, setLinkedHoldings] = useState([]);
  const [linkedCashHoldings, setLinkedCashHoldings] = useState([]);
  const [manualAssets, setManualAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [lastSynced, setLastSynced] = useState(null);
  const [accountFilter, setAccountFilter] = useState("all");
  const [showManualForm, setShowManualForm] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [manualDraft, setManualDraft] = useState(EMPTY_MANUAL_ASSET);
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    fetchHoldings();
    refreshManualAssets();
  }, []);

  const refreshManualAssets = async () => {
    try {
      const res = await listManualAssets();
      setManualAssets(res.data?.assets || []);
    } catch (err) {
      console.error("Could not load manual assets:", err);
    }
  };

  const fetchHoldings = async () => {
    try {
      setLoading(true);
      const [holdingsResponse, accountsResponse] = await Promise.allSettled([
        getSnapTradeHoldings(),
        getSnapTradeAccounts(),
      ]);

      if (holdingsResponse.status === "fulfilled") {
        setLinkedHoldings((holdingsResponse.value.data.holdings || []).map(normalizeBrokerageHolding));
        setLastSynced(holdingsResponse.value.data.lastSynced);
      } else {
        setLinkedHoldings([]);
      }

      if (accountsResponse.status === "fulfilled") {
        setLinkedCashHoldings(
          (accountsResponse.value.data.accounts || [])
            .map(normalizeAccountBalance)
            .filter((holding) => Math.abs(holding.marketValue) > 0)
        );
      } else {
        setLinkedCashHoldings([]);
      }

      setError("");
    } catch (err) {
      console.error("Error fetching holdings:", err);
      setError("Linked holdings could not be loaded. Manual assets are still available.");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncHoldings = async () => {
    try {
      setSyncing(true);
      setError("");
      await syncSnapTradeHoldings();
      await fetchHoldings();
    } catch (err) {
      console.error("Error syncing holdings:", err);
      setError(err.response?.data?.detail || "Failed to sync linked accounts.");
    } finally {
      setSyncing(false);
    }
  };

  const addManualAsset = async (event) => {
    event.preventDefault();
    if (!manualDraft.name.trim()) return;

    const derivedLandValue = numberValue(manualDraft.area) * numberValue(manualDraft.localPricePerUnit);
    const currentValue = numberValue(manualDraft.currentValue) || derivedLandValue;
    const payload = {
      name: manualDraft.name.trim(),
      symbol: (manualDraft.symbol || "").trim(),
      asset_class: TYPE_TO_ASSET_CLASS[manualDraft.type] || "other",
      cost_basis: numberValue(manualDraft.costBasis),
      current_value: currentValue,
      currency: manualDraft.currency || "USD",
      acquired_at: manualDraft.purchaseDate || null,
      notes: manualDraft.notes || "",
      metadata: {
        location: manualDraft.location || "",
        area: numberValue(manualDraft.area),
        areaUnit: manualDraft.areaUnit || "sq ft",
        localPricePerUnit: numberValue(manualDraft.localPricePerUnit),
      },
    };

    try {
      setSavingManual(true);
      setError("");
      await createManualAsset(payload);
      await refreshManualAssets();
      setManualDraft(EMPTY_MANUAL_ASSET);
      setShowManualForm(false);
    } catch (err) {
      console.error("Could not save manual asset:", err);
      setError(err.response?.data?.detail || "Could not save manual asset.");
    } finally {
      setSavingManual(false);
    }
  };

  const removeManualAsset = async (id) => {
    try {
      await deleteManualAsset(id);
      await refreshManualAssets();
    } catch (err) {
      console.error("Could not delete manual asset:", err);
      setError("Could not delete that asset. Try again.");
    }
  };

  const normalizedManualAssets = useMemo(() => manualAssets.map(normalizeManualAsset), [manualAssets]);
  const allHoldings = useMemo(
    () => [...linkedHoldings, ...linkedCashHoldings, ...normalizedManualAssets],
    [linkedHoldings, linkedCashHoldings, normalizedManualAssets]
  );

  const summary = useMemo(() => {
    const totalValue = allHoldings.reduce((sum, holding) => sum + holding.valueUSD, 0);
    const totalCost = allHoldings.reduce((sum, holding) => sum + holding.costBasisUSD, 0);
    const totalGainLoss = allHoldings.reduce((sum, holding) => sum + holding.unrealizedPLUSD, 0);

    return {
      totalValue,
      totalGainLoss,
      totalGainLossPercent: totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0,
      numberOfHoldings: allHoldings.length,
      linkedCount: linkedHoldings.length + linkedCashHoldings.length,
      manualCount: manualAssets.length,
    };
  }, [allHoldings, linkedCashHoldings.length, linkedHoldings.length, manualAssets.length]);

  const sectorAllocation = useMemo(() => {
    const totals = {};
    allHoldings.forEach((holding) => {
      const sector = holding.sector || holding.assetType || "Other";
      totals[sector] = (totals[sector] || 0) + holding.valueUSD;
    });

    return Object.entries(totals)
      .map(([sector, value]) => ({
        sector,
        value,
        percentage: summary.totalValue > 0 ? (value / summary.totalValue) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [allHoldings, summary.totalValue]);

  const accounts = useMemo(
    () => ["all", ...new Set(allHoldings.map((holding) => holding.account || holding.source))],
    [allHoldings]
  );
  const filteredHoldings =
    accountFilter === "all" ? allHoldings : allHoldings.filter((holding) => holding.account === accountFilter);

  return (
    <div className="min-h-screen pb-20 relative animate-fadeIn">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-2 text-gray-900 dark:text-white">
              Holdings
            </h2>
            <p className="text-gray-500 dark:text-gray-400 font-medium max-w-3xl">
              This is your asset ledger. Linked accounts add market assets automatically; manual assets cover land,
              cash, gold, private business, and anything else you own.
            </p>
            {lastSynced && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 font-mono">
                Last linked-account sync: {new Date(lastSynced).toLocaleString()}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setShowDocumentUpload(true)}
              title="Upload a brokerage statement, FD certificate, or property deed and FinAI will propose asset rows."
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 16a4 4 0 01-.88-7.9A5 5 0 0115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload PDF
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setShowManualForm((current) => !current)}
            >
              {showManualForm ? "Close Manual Asset" : "Add Manual Asset"}
            </Button>
            <Button
              size="lg"
              onClick={handleSyncHoldings}
              disabled={syncing || loading}
            >
              {syncing ? "Syncing..." : "Sync Linked Accounts"}
            </Button>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-xl">
            {error}
          </div>
        )}

        {showManualForm && (
          <form
            onSubmit={addManualAsset}
            className="p-6 rounded-2xl bg-white/70 dark:bg-gray-900/70 border border-gray-200 dark:border-white/10 shadow-sm"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-5">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Manual Asset</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Synced to your account and visible to the AI. Use this for land, gold, FDs, private business — anything outside a linked brokerage.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <input
                value={manualDraft.name}
                onChange={(event) => setManualDraft({ ...manualDraft, name: event.target.value })}
                placeholder="Asset name"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                value={manualDraft.symbol}
                onChange={(event) => setManualDraft({ ...manualDraft, symbol: event.target.value })}
                placeholder="Symbol or short label"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={manualDraft.type}
                onChange={(event) => setManualDraft({ ...manualDraft, type: event.target.value })}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ASSET_TYPES.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
              <select
                value={manualDraft.currency}
                onChange={(event) => setManualDraft({ ...manualDraft, currency: event.target.value })}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.keys(CURRENCY_TO_USD).map((currencyCode) => (
                  <option key={currencyCode}>{currencyCode}</option>
                ))}
              </select>
              <input
                value={manualDraft.costBasis}
                onChange={(event) => setManualDraft({ ...manualDraft, costBasis: event.target.value })}
                placeholder="Cost basis"
                inputMode="decimal"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                value={manualDraft.currentValue}
                onChange={(event) => setManualDraft({ ...manualDraft, currentValue: event.target.value })}
                placeholder="Current value"
                inputMode="decimal"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="date"
                value={manualDraft.purchaseDate}
                onChange={(event) => setManualDraft({ ...manualDraft, purchaseDate: event.target.value })}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                value={manualDraft.notes}
                onChange={(event) => setManualDraft({ ...manualDraft, notes: event.target.value })}
                placeholder="Notes"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <input
                value={manualDraft.location}
                onChange={(event) => setManualDraft({ ...manualDraft, location: event.target.value })}
                placeholder="Location for land/property"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                value={manualDraft.area}
                onChange={(event) => setManualDraft({ ...manualDraft, area: event.target.value })}
                placeholder="Area"
                inputMode="decimal"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={manualDraft.areaUnit}
                onChange={(event) => setManualDraft({ ...manualDraft, areaUnit: event.target.value })}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>sq ft</option>
                <option>sq yd</option>
                <option>acre</option>
                <option>cent</option>
                <option>ground</option>
                <option>hectare</option>
              </select>
              <input
                value={manualDraft.localPricePerUnit}
                onChange={(event) => setManualDraft({ ...manualDraft, localPricePerUnit: event.target.value })}
                placeholder="Nearby price / unit"
                inputMode="decimal"
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              For land, you can enter either current value directly or area plus nearby price per unit.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={savingManual}
                className={`px-5 py-2.5 rounded-lg text-white font-semibold ${
                  savingManual ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {savingManual ? "Saving..." : "Save Asset"}
              </button>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-6 rounded-2xl bg-white/60 dark:bg-gray-900/60 border border-white/40 dark:border-white/10 shadow-sm">
            <p className="text-sm font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase mb-2">
              Total Value
            </p>
            <p className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">
              {formatCurrencyShared(
                convertAmount(summary.totalValue, "USD", displayCurrency),
                displayCurrency,
              )}
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/60 dark:bg-gray-900/60 border border-white/40 dark:border-white/10 shadow-sm">
            <p className="text-sm font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase mb-2">
              Total Gain/Loss
            </p>
            <p className={`text-2xl sm:text-3xl font-extrabold ${summary.totalGainLoss >= 0 ? "text-green-500" : "text-red-500"}`}>
              {formatCurrencyShared(
                convertAmount(summary.totalGainLoss, "USD", displayCurrency),
                displayCurrency,
              )}
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/60 dark:bg-gray-900/60 border border-white/40 dark:border-white/10 shadow-sm">
            <p className="text-sm font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase mb-2">
              Net Return
            </p>
            <p
              className={`text-2xl sm:text-3xl font-extrabold ${
                summary.totalGainLossPercent >= 0 ? "text-green-500" : "text-red-500"
              }`}
            >
              {formatPercent(summary.totalGainLossPercent)}
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/60 dark:bg-gray-900/60 border border-white/40 dark:border-white/10 shadow-sm">
            <p className="text-sm font-semibold tracking-wider text-gray-500 dark:text-gray-400 uppercase mb-2">
              Assets
            </p>
            <p className="text-2xl sm:text-3xl font-extrabold text-blue-500">{summary.numberOfHoldings}</p>
            <p className="text-xs text-gray-500 mt-2">
              {summary.linkedCount} linked, {summary.manualCount} manual
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="p-5 sm:p-7 rounded-2xl bg-white/60 dark:bg-gray-900/60 border border-white/40 dark:border-white/10 shadow-sm">
            <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">Asset Class Distribution</h3>
            {sectorAllocation.length > 0 ? (
              <div className="space-y-5">
                {sectorAllocation.map((sector) => (
                  <div key={sector.sector}>
                    <div className="flex justify-between text-sm mb-2 font-medium">
                      <span className="text-gray-700 dark:text-gray-300">{sector.sector}</span>
                      <span className="text-gray-900 dark:text-white font-bold">{sector.percentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200/70 dark:bg-gray-800 rounded-full h-2 overflow-hidden">
                      <div className="h-full rounded-full bg-blue-500" style={{ width: `${sector.percentage}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No holdings yet.</p>
            )}
          </div>

          <div className="lg:col-span-2 p-5 sm:p-7 rounded-2xl bg-white/60 dark:bg-gray-900/60 border border-white/40 dark:border-white/10 shadow-sm overflow-hidden">
            <div className="flex flex-wrap justify-between items-center mb-6 gap-4 border-b border-gray-200 dark:border-white/10 pb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Holdings Ledger</h3>
              <select
                value={accountFilter}
                onChange={(event) => setAccountFilter(event.target.value)}
                className="px-4 py-2 bg-white/70 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-xl text-sm font-medium dark:text-white outline-none"
              >
                {accounts.map((account) => (
                  <option key={account} value={account}>
                    {account === "all" ? "All Holdings" : account}
                  </option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="py-12 text-center text-gray-500">Loading linked holdings...</div>
            ) : filteredHoldings.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                No holdings yet. Link accounts from Linked Accounts or add a manual asset here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="py-3 px-3 sm:px-4 text-left">Asset</th>
                      <th className="py-3 px-3 sm:px-4 text-left hidden md:table-cell">Source</th>
                      <th className="py-3 px-3 sm:px-4 text-left hidden lg:table-cell">Type</th>
                      <th className="py-3 px-3 sm:px-4 text-right">Value</th>
                      <th className="py-3 px-3 sm:px-4 text-right hidden sm:table-cell">Cost</th>
                      <th className="py-3 px-3 sm:px-4 text-right">P/L</th>
                      <th className="py-3 px-3 sm:px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/5 border-t border-gray-100 dark:border-white/5">
                    {filteredHoldings.map((holding) => (
                      <tr key={holding.id} className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
                        <td className="py-4 px-3 sm:px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center font-bold text-gray-700 dark:text-gray-300">
                              {(holding.symbol || "AS").substring(0, 2)}
                            </div>
                            <div>
                              <div className="font-bold text-gray-900 dark:text-white tracking-wide flex items-center gap-2">
                                {holding.symbol}
                                <button
                                  onClick={() =>
                                    window.dispatchEvent(
                                      new CustomEvent("council:askPrefill", {
                                        detail: {
                                          prefill:
                                            holding.source === "Manual"
                                              ? `Walk me through my "${holding.name}" (${holding.assetType}, value ${formatCurrency(holding.marketValue, holding.currency)}, cost basis ${formatCurrency(holding.costBasis, holding.currency)}). How does this fit my overall portfolio?`
                                              : `Walk me through my position in ${holding.symbol} (${holding.name}). Current value ${formatCurrency(holding.marketValue, holding.currency)}, P/L ${formatPercent(holding.unrealizedPLPercent)}. Should I hold, trim, or add?`,
                                        },
                                      }),
                                    )
                                  }
                                  className="w-6 h-6 rounded-full bg-purple-500/10 hover:bg-purple-500 flex items-center justify-center text-purple-500 hover:text-white transition"
                                  title="Ask the Council about this asset"
                                >
                                  <SparklesIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[190px]">
                                {holding.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-3 sm:px-4 hidden md:table-cell">
                          <span className="px-3 py-1 rounded-md text-xs font-semibold bg-blue-100/60 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
                            {holding.source}
                          </span>
                        </td>
                        <td className="py-4 px-3 sm:px-4 text-gray-700 dark:text-gray-300 hidden lg:table-cell">{holding.assetType}</td>
                        <td className="py-4 px-3 sm:px-4 text-right font-bold text-gray-900 dark:text-white">
                          {formatCurrency(holding.marketValue, holding.currency)}
                        </td>
                        <td className="py-4 px-3 sm:px-4 text-right text-gray-700 dark:text-gray-300 hidden sm:table-cell">
                          {formatCurrency(holding.costBasis, holding.currency)}
                        </td>
                        <td className="py-4 px-3 sm:px-4 text-right">
                          <div className={`font-bold ${holding.unrealizedPL >= 0 ? "text-green-500" : "text-red-500"}`}>
                            {formatPercent(holding.unrealizedPLPercent)}
                          </div>
                          <div className="text-[11px] font-medium text-gray-500 opacity-80 mt-0.5">
                            {formatCurrency(holding.unrealizedPL, holding.currency)}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right">
                          {holding.source === "Manual" ? (
                            <button
                              onClick={() => removeManualAsset(holding.id)}
                              className="px-3 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300"
                            >
                              Remove
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">Managed by link</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <WatchlistCard />
        </div>
        <Link
          to="/portfolio/tax"
          className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/15 to-amber-500/5 border border-amber-500/40 hover:from-amber-500/20 hover:to-amber-500/10 transition group flex flex-col justify-center"
        >
          <div className="text-amber-500 font-bold text-xs uppercase tracking-widest mb-2">
            Tax-loss harvest
          </div>
          <div className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1 group-hover:translate-x-0.5 transition-transform">
            Find harvest candidates →
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Tally scans your positions for unrealized losses, computes the tax saving for
            your jurisdiction, and lets you decide.
          </p>
        </Link>
      </div>

      <DocumentUploadModal
        open={showDocumentUpload}
        onClose={() => setShowDocumentUpload(false)}
        onSaved={async () => {
          await refreshManualAssets();
        }}
      />

    </div>
  );
};

export default Holdings;
